import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { downloadFile } from "@/lib/download";
import TradeLicenseReminder from "@/components/TradeLicenseReminder";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ReceiptText, FileSpreadsheet, Plus, Loader2, Building2, FileText, ArrowRight,
  CheckCircle2, Info, ShieldCheck, Download, Link2, Unplug,
} from "lucide-react";

const PLAN_META = {
  payg: { name: "Pay as you go", price: "AED 0.15 / invoice" },
  starter: { name: "Starter", price: "AED 100 / year · 1,000 incl." },
  growth: { name: "Growth", price: "AED 300 / year · 3,000 incl." },
  pro: { name: "Pro", price: "AED 600 / year · 6,000 incl." },
  full_service: { name: "Full Service", price: "To be confirmed" },
};

const MANUAL_PLANS = ["payg", "starter", "growth", "pro"];

const EMPTY_CONTRACT = {
  title: "", project_name: "", contract_ref: "", contract_date: "",
  retention_pct: 10, advance_pct: 10,
  buyer: { company_name: "", trn: "", address: "", city: "", peppol_id: "" },
};

export const peppolFromTrn = (trn) => {
  const d = (trn || "").replace(/\D/g, "");
  return d.length >= 10 ? `0235:${d.slice(0, 10)}` : "";
};
export const trnDigits = (trn) => (trn || "").replace(/\D/g, "");

export default function EInvoicingApp() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_CONTRACT);
  const [saving, setSaving] = useState(false);
  const [busyPlan, setBusyPlan] = useState("");
  const [wafeq, setWafeq] = useState(null);
  const [wafeqBusy, setWafeqBusy] = useState(false);

  const loadWafeq = useCallback(() => {
    api.get("/einvoice/wafeq/status").then((r) => setWafeq(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    Promise.all([
      api.get("/einvoice/subscription"),
      api.get("/einvoice/plans"),
      api.get("/einvoice/contracts"),
    ]).then(([s, p, c]) => {
      setSub(s.data); setPlans(p.data.plans); setContracts(c.data.contracts);
      if (s.data.plan === "full_service" || s.data.free) loadWafeq();
    }).catch(() => {}).finally(() => setLoading(false));
  }, [loadWafeq]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const w = params.get("wafeq");
    if (w === "connected") { toast.success("Connected to the FTA via Wafeq"); setParams({}, { replace: true }); loadWafeq(); }
    else if (w === "error") { toast.error("Wafeq connection failed — please try again"); setParams({}, { replace: true }); }
  }, [params, setParams, loadWafeq]);

  const connectWafeq = async () => {
    setWafeqBusy(true);
    try {
      const r = await api.post("/einvoice/wafeq/connect");
      if (r.data.authorize_url) window.location.href = r.data.authorize_url;
      else { toast.error("Could not start the Wafeq connection"); setWafeqBusy(false); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the Wafeq connection");
      setWafeqBusy(false);
    }
  };

  const disconnectWafeq = async () => {
    if (!window.confirm("Disconnect this business from Wafeq? New submissions will stop until you reconnect.")) return;
    setWafeqBusy(true);
    try {
      await api.post("/einvoice/wafeq/disconnect");
      toast.success("Disconnected from Wafeq");
      loadWafeq();
    } catch { toast.error("Could not disconnect"); }
    finally { setWafeqBusy(false); }
  };

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) {
      api.get(`/einvoice/subscription/status/${sid}`)
        .then((r) => {
          if (r.data.payment_status === "paid") {
            toast.success("E-invoicing subscription active");
            setSub(r.data.subscription);
          }
        })
        .catch(() => {})
        .finally(() => { setParams({}); load(); });
    } else if (params.get("checkout") === "cancel") {
      toast.info("Checkout cancelled");
      setParams({});
    }
  }, [params, setParams, load]);

  const subscribe = async (planKey) => {
    setBusyPlan(planKey);
    try {
      const r = await api.post("/einvoice/subscription/select", { plan: planKey });
      setSub(r.data);
      toast.success(planKey === "payg" ? "Switched to Pay as you go" : `${PLAN_META[planKey]?.name || "Plan"} activated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change plan");
    } finally {
      setBusyPlan("");
    }
  };

  const createContract = async () => {
    if (!form.title.trim()) { toast.error("Give the contract a title"); return; }
    setSaving(true);
    try {
      const r = await api.post("/einvoice/contracts", form);
      setOpen(false); setForm(EMPTY_CONTRACT);
      navigate(`/einvoicing/contract/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create the contract");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div data-testid="einvoicing-app">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.16em] text-brand font-bold">UAE FTA · Peppol PINT-AE</div>
        <div className="mt-2 flex items-center gap-3">
          <img src="/matiqs-qonnect-logo.png" alt="Matiqs Qonnect" data-testid="qonnect-logo" className="brand-logo h-9 w-auto" />
          <h1 className="sr-only">Matiqs Qonnect</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          UAE FTA-compliant tax invoices for any business. Create a straightforward standard invoice,
          or bill a construction contract through Interim Payment Certificates — exported as PDF, Excel,
          CSV and Peppol UBL XML.
        </p>
      </div>

      <TradeLicenseReminder profileTo="/settings" className="mb-6" />

      {/* Two-path entry */}
      <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="qonnect-paths">
        <button data-testid="path-standard" onClick={() => navigate("/einvoicing/standard")}
                className="text-left rounded-2xl border border-border bg-card p-5 hover:border-brand hover:shadow-md transition-all group">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-brand-soft flex items-center justify-center shrink-0"><ReceiptText className="w-5 h-5 text-brand" /></div>
            <div>
              <div className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">Standard invoice <ArrowRight className="w-4 h-4 text-brand opacity-0 group-hover:opacity-100 transition-opacity" /></div>
              <div className="text-xs text-muted-foreground">Any business — no contract or BOQ. Add line items and go.</div>
            </div>
          </div>
        </button>
        <a href="#construction" data-testid="path-construction"
           className="text-left rounded-2xl border border-border bg-card p-5 hover:border-brand hover:shadow-md transition-all group block">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-brand-soft flex items-center justify-center shrink-0"><FileText className="w-5 h-5 text-brand" /></div>
            <div>
              <div className="font-bold text-slate-900 dark:text-white">Construction contracts &amp; IPCs</div>
              <div className="text-xs text-muted-foreground">Contract → BOQ → Interim Payment Certificates, with retention &amp; variations.</div>
            </div>
          </div>
        </a>
      </div>

      {/* Wafeq FTA connection — Full Service */}
      {(sub.plan === "full_service" || sub.free) && (
        <div data-testid="wafeq-connect-card" className="rounded-2xl border border-border bg-card p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${wafeq?.connected ? "bg-emerald-500/10" : "bg-indigo-500/10"}`}>
              {wafeq?.connected ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Link2 className="w-5 h-5 text-indigo-500" />}
            </div>
            <div>
              {wafeq?.connected ? (
                <>
                  <div className="font-bold text-slate-900 dark:text-white flex items-center gap-2" data-testid="wafeq-connected-label">
                    Connected to FTA via our Approved ASP
                    <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">{wafeq.mode_label}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{wafeq.tin ? `TRN ${wafeq.tin} · ` : ""}Submissions are sent live to the FTA through our Approved ASP.</div>
                </>
              ) : (
                <>
                  <div className="font-bold text-slate-900 dark:text-white" data-testid="wafeq-disconnected-label">Connect to FTA</div>
                  <div className="text-xs text-muted-foreground">
                    {wafeq?.configured
                      ? "Authorise Matiqs to submit this business's invoices to the FTA through our Approved ASP."
                      : "Your Approved ASP isn't configured yet — submissions run in Simulation until the Matiqs team adds the ASP credentials."}
                  </div>
                </>
              )}
            </div>
          </div>
          {wafeq?.connected ? (
            <Button data-testid="wafeq-disconnect-btn" variant="outline" size="sm" className="gap-1.5" disabled={wafeqBusy} onClick={disconnectWafeq}>
              {wafeqBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unplug className="w-4 h-4" />} Disconnect
            </Button>
          ) : (
            <Button data-testid="wafeq-connect-btn" size="sm" className="gap-1.5" disabled={wafeqBusy || !wafeq?.configured} onClick={connectWafeq}>
              {wafeqBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} {wafeq?.configured ? "Connect to Approved ASP" : "Not configured"}
            </Button>
          )}
        </div>
      )}

      {/* ASP clearance entry — Full Service */}
      {(sub.plan === "full_service" || sub.free) && (
        <button data-testid="path-clearance" onClick={() => navigate("/einvoicing/clearance")}
                className="w-full text-left rounded-2xl border border-indigo-300 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20 p-4 mb-6 hover:border-indigo-400 hover:shadow-md transition-all group flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-indigo-500/10 flex items-center justify-center shrink-0"><ShieldCheck className="w-5 h-5 text-indigo-500" /></div>
            <div>
              <div className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">ASP clearance <ArrowRight className="w-4 h-4 text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
              <div className="text-xs text-muted-foreground">Track every invoice submitted to the FTA via our Approved ASP — standard &amp; construction — and its clearance status.</div>
            </div>
          </div>
        </button>
      )}

      {/* Templates */}
      <div id="construction" className="flex flex-wrap items-center gap-3 mb-4 pt-2">
        <span className="text-xs font-semibold text-muted-foreground">Construction template:</span>
        <Button variant="outline" size="sm" data-testid="dl-contract-template"
                onClick={() => downloadFile("/einvoice/boq-template?kind=contract", "matiqs-contract-boq-template.xlsx")}
                className="gap-1.5"><FileSpreadsheet className="w-3.5 h-3.5" /> Contract BOQ (high level)</Button>
      </div>

      {/* Construction marketplace callout */}
      <a href="/marketplace" data-testid="marketplace-callout"
         className="flex items-center justify-between gap-3 rounded-xl border border-indigo-300/50 bg-indigo-50/50 dark:bg-indigo-950/20 px-4 py-3 mb-6 hover:border-indigo-400 transition-colors">
        <div className="flex items-center gap-2.5">
          <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400 shrink-0" />
          <span className="text-sm text-slate-700 dark:text-slate-300"><strong>Construction business?</strong> You also get the Matiqs construction procurement marketplace — source materials and manage RFQs.</span>
        </div>
        <ArrowRight className="w-4 h-4 text-indigo-600 dark:text-indigo-400 shrink-0" />
      </a>

      {/* Contracts */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white">Your construction contracts</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-contract-btn" className="gap-1.5"><Plus className="w-4 h-4" /> New contract</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>New contract</DialogTitle>
              <DialogDescription>Set up a contract once — upload its BOQ next, then invoice each IPC.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Contract title *</Label>
                <Input data-testid="contract-title-input" value={form.title}
                       onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Tower A — Main Works" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Project</Label><Input value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} placeholder="Downtown Tower A" /></div>
                <div><Label>Contract / PO ref</Label><Input data-testid="contract-ref-input" value={form.contract_ref} onChange={(e) => setForm({ ...form, contract_ref: e.target.value })} placeholder="CTR-2026-001" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Contract date</Label><Input type="date" data-testid="contract-date-input" value={form.contract_date} onChange={(e) => setForm({ ...form, contract_date: e.target.value })} /></div>
                <div><Label>Retention %</Label><Input type="number" value={form.retention_pct} onChange={(e) => setForm({ ...form, retention_pct: e.target.value })} /></div>
                <div><Label>Advance %</Label><Input type="number" value={form.advance_pct} onChange={(e) => setForm({ ...form, advance_pct: e.target.value })} /></div>
              </div>
              <div className="pt-1 border-t border-border">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-3 mb-2">Buyer (billed party)</div>
                <div className="space-y-3">
                  <div><Label>Company name</Label><Input data-testid="buyer-name-input" value={form.buyer.company_name} onChange={(e) => setForm({ ...form, buyer: { ...form.buyer, company_name: e.target.value } })} placeholder="Prime Developers LLC" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Buyer TRN</Label>
                      <Input data-testid="buyer-trn-input" value={form.buyer.trn} onChange={(e) => {
                        const v = e.target.value;
                        const prevDerived = peppolFromTrn(form.buyer.trn);
                        const keep = form.buyer.peppol_id && form.buyer.peppol_id !== prevDerived;
                        setForm({ ...form, buyer: { ...form.buyer, trn: v, peppol_id: keep ? form.buyer.peppol_id : peppolFromTrn(v) } });
                      }} placeholder="15 digits" />
                      {trnDigits(form.buyer.trn).length > 0 && trnDigits(form.buyer.trn).length !== 15 && (
                        <p data-testid="buyer-trn-warn" className="text-[11px] text-amber-600 mt-1">TRN must be 15 digits (currently {trnDigits(form.buyer.trn).length}).</p>
                      )}
                    </div>
                    <div><Label>City / Emirate</Label><Input value={form.buyer.city} onChange={(e) => setForm({ ...form, buyer: { ...form.buyer, city: e.target.value } })} placeholder="Dubai" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Recipient Peppol ID</Label>
                      <Input data-testid="buyer-peppol-input" className="font-mono" value={form.buyer.peppol_id} onChange={(e) => setForm({ ...form, buyer: { ...form.buyer, peppol_id: e.target.value } })} placeholder={peppolFromTrn(form.buyer.trn) || "0235:XXXXXXXXXX"} />
                      {peppolFromTrn(form.buyer.trn) && (
                        <p className="text-[11px] text-muted-foreground mt-1">Auto-filled from TRN — editable.</p>
                      )}
                    </div>
                    <div><Label>Address</Label><Input data-testid="buyer-address-input" value={form.buyer.address} onChange={(e) => setForm({ ...form, buyer: { ...form.buyer, address: e.target.value } })} /></div>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="create-contract-submit" onClick={createContract} disabled={saving} className="gap-1.5">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create contract
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {contracts.length === 0 ? (
        <div data-testid="contracts-empty" className="text-center py-16 border border-dashed border-border rounded-2xl">
          <Building2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No contracts yet. Create one, upload its BOQ, then invoice each IPC.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="contracts-list">
          {contracts.map((c) => (
            <button key={c.id} data-testid={`contract-card-${c.id}`}
                    onClick={() => navigate(`/einvoicing/contract/${c.id}`)}
                    className="text-left rounded-2xl border border-border bg-card p-5 hover:border-primary/50 hover:shadow-md transition-all">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 dark:text-white truncate">{c.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.buyer?.company_name || "No buyer set"} · {c.contract_ref || "no ref"}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-4 text-xs">
                <span className="flex items-center gap-1 text-muted-foreground"><FileText className="w-3.5 h-3.5" /> {c.boq_lines} BOQ lines</span>
                <span className="flex items-center gap-1 text-muted-foreground"><ReceiptText className="w-3.5 h-3.5" /> {c.ipc_count} IPC{c.ipc_count === 1 ? "" : "s"}</span>
                {c.boq_lines > 0
                  ? <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" /> BOQ ready</span>
                  : <span className="flex items-center gap-1 text-amber-600"><Info className="w-3.5 h-3.5" /> BOQ pending</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Subscription — plans (Full Service featured) */}
      <div data-testid="einv-subscription-panel" className="rounded-2xl border border-border bg-card p-5 mt-8 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Current plan</div>
            <div className="text-lg font-bold text-slate-900 dark:text-white mt-1" data-testid="einv-current-plan">
              {MANUAL_PLANS.includes(sub.plan) ? "Full Service" : (PLAN_META[sub.plan]?.name || sub.plan_name)}
            </div>
            <div className="text-xs text-muted-foreground">{MANUAL_PLANS.includes(sub.plan) ? "To be confirmed" : PLAN_META[sub.plan]?.price}</div>
          </div>
          <div className="text-right">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">This month ({sub.month})</div>
            <div className="text-lg font-bold text-slate-900 dark:text-white mt-1" data-testid="einv-usage">
              {sub.used}{(!MANUAL_PLANS.includes(sub.plan) && sub.included) ? ` / ${sub.included}` : ""} e-invoices
            </div>
            <div className="text-xs text-muted-foreground">
              {sub.free
                ? "Free for testing — no charge"
                : MANUAL_PLANS.includes(sub.plan)
                  ? "Pricing to be confirmed"
                  : sub.included === null
                    ? "Unlimited · pricing to be confirmed"
                    : sub.included === 0
                      ? `AED ${sub.payg_amount.toFixed(2)} this month · AED 0.15 each`
                      : sub.overage_count > 0
                        ? `${sub.overage_count} over the annual allowance · AED ${sub.overage_amount.toFixed(2)}`
                        : `Within your ${sub.included.toLocaleString()}/year allowance`}
            </div>
          </div>
        </div>

        {sub.free ? (
          <div data-testid="einv-free-note" className="mt-5 flex items-center gap-2 rounded-xl border border-emerald-300 dark:border-emerald-900 bg-emerald-50/70 dark:bg-emerald-950/30 px-4 py-3 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="w-4 h-4 shrink-0" /> Admin account — e-invoicing is free and unlimited for testing. No subscription needed.
          </div>
        ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-5">
          {[...plans].sort((a, b) => (b.recommended ? 1 : 0) - (a.recommended ? 1 : 0)).map((p) => {
            const active = sub.plan === p.key;
            const locked = p.coming_soon || p.selectable === false;
            const featured = p.recommended && !active;
            return (
              <div key={p.key} data-testid={`einv-plan-${p.key}`} className={`rounded-xl border p-4 relative ${active ? "border-primary ring-1 ring-primary/30 bg-primary/5" : featured ? "border-indigo-400 dark:border-indigo-700 ring-1 ring-indigo-300/50 bg-indigo-50/40 dark:bg-indigo-950/20" : "border-border"}`}>
                {p.recommended && <span data-testid={`einv-recommended-${p.key}`} className="absolute -top-2 right-3 px-2 py-0.5 rounded-full bg-indigo-600 text-white text-[9px] font-bold uppercase tracking-wider">Recommended</span>}
                <div className="flex items-center gap-1.5">
                  <div className="font-bold text-sm text-slate-900 dark:text-white">{p.name}</div>
                  {p.coming_soon && <span data-testid={`einv-coming-soon-${p.key}`} className="px-1.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 text-[9px] font-bold uppercase tracking-wider">Soon</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{p.blurb}</div>
                <Button
                  data-testid={`einv-subscribe-${p.key}`}
                  disabled={active || busyPlan === p.key || locked}
                  variant={active ? "secondary" : featured ? "default" : "outline"}
                  size="sm"
                  onClick={() => subscribe(p.key)}
                  className="w-full mt-3 font-semibold"
                >
                  {busyPlan === p.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : locked ? "Coming soon" : active ? "Current" : (p.key === "payg" ? "Switch" : "Choose")}
                </Button>
              </div>
            );
          })}
        </div>
        )}
      </div>

      <div className="flex items-start gap-2 text-xs text-muted-foreground mt-8">
        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-emerald-500" />
        <span>Matiqs Qonnect generates UAE FTA (Peppol PINT-AE) compliant documents and submits them to the FTA through our Approved Service Provider. On Full Service, connect your business and we transmit and clear each invoice on your behalf.</span>
      </div>
    </div>
  );
}
